/**
 * useOfferingInstances Hooks
 * Phase 2: Class & Webinar Schedules
 * Manage training_offering_instances - metadata only, no enrollment.
 * 
 * IMPORTANT: No enrollment counts or registration tracking.
 * Links to external registration_url only.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
// DTC-L: route offering-instance window anchors through clock.
import { clock } from '@/utils/clock';

export interface TrainingOfferingInstance {
  id: string;
  artifact_id: string;
  provider_id: string;
  start_datetime: string;
  end_datetime: string | null;
  timezone: string;
  delivery_type: 'online' | 'in_person' | 'hybrid';
  city: string | null;
  state: string | null;
  venue_name: string | null;
  registration_url: string;
  is_paid: boolean;
  price_text: string | null;
  capacity_text: string | null; // Text only, not numeric - NO enrollment tracking
  status: 'upcoming' | 'ongoing' | 'completed' | 'cancelled';
  created_at: string;
  updated_at: string;
  // Joined artifact info
  artifact?: {
    id: string;
    title: string;
    provider_name: string | null;
  };
}

interface OfferingFilters {
  artifactId?: string;
  providerId?: string;
  status?: TrainingOfferingInstance['status'];
  statusIn?: string[];
  limit?: number;
}

/**
 * Fetch offering instances for admin view
 */
export function useOfferingInstances(filters?: OfferingFilters) {
  return useQuery({
    queryKey: ['offering-instances', filters],
    queryFn: async () => {
      let query = supabase
        .from('training_offering_instances')
        .select(`
          *,
          artifact:training_artifacts(id, title, provider_name)
        `)
        .order('start_datetime', { ascending: true });

      if (filters?.artifactId) {
        query = query.eq('artifact_id', filters.artifactId);
      }
      if (filters?.providerId) {
        query = query.eq('provider_id', filters.providerId);
      }
      if (filters?.statusIn && filters.statusIn.length > 0) {
        query = query.in('status', filters.statusIn);
      } else if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.limit) {
        query = query.limit(filters.limit);
      }

      const { data, error } = await query;
      if (error) throw error;

      return (data || []) as TrainingOfferingInstance[];
    },
  });
}

/**
 * Fetch upcoming offerings for specific artifacts (for recommendation integration)
 * Only returns offerings that:
 * - Are in the future
 * - Have status = 'upcoming'
 * - Have a registration_url
 */
export function useUpcomingOfferings(
  artifactIds: string[],
  options?: {
    withinDays?: number;
    timezone?: string;
    limit?: number;
  }
) {
  const withinDays = options?.withinDays || 60;

  return useQuery({
    queryKey: ['offering-instances', 'upcoming', artifactIds, options],
    queryFn: async () => {
      if (!artifactIds || artifactIds.length === 0) return [];

      const now = clock.now();
      const futureDate = clock.now();
      futureDate.setDate(futureDate.getDate() + withinDays);

      const { data, error } = await supabase
        .from('training_offering_instances')
        .select(`
          id,
          artifact_id,
          start_datetime,
          end_datetime,
          timezone,
          delivery_type,
          city,
          state,
          venue_name,
          registration_url,
          status
        `)
        .in('artifact_id', artifactIds)
        .eq('status', 'upcoming')
        .eq('is_active', true)
        .gte('start_datetime', now.toISOString())
        .lte('start_datetime', futureDate.toISOString())
        .not('registration_url', 'is', null)
        .order('start_datetime', { ascending: true });

      if (error) throw error;

      // Limit results per artifact
      const limitPerArtifact = options?.limit || 3;
      const groupedByArtifact = new Map<string, typeof data>();
      
      for (const offering of data || []) {
        const existing = groupedByArtifact.get(offering.artifact_id) || [];
        if (existing.length < limitPerArtifact) {
          existing.push(offering);
          groupedByArtifact.set(offering.artifact_id, existing);
        }
      }

      // Flatten back to array
      return Array.from(groupedByArtifact.values()).flat() as TrainingOfferingInstance[];
    },
    enabled: artifactIds.length > 0,
  });
}

/**
 * Create a new offering instance
 */
export function useCreateOffering() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (offering: Partial<TrainingOfferingInstance> & {
      artifact_id: string;
      provider_id: string;
      start_datetime: string;
      registration_url: string;
    }) => {
      const { data, error } = await supabase
        .from('training_offering_instances')
        .insert(offering as any)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offering-instances'] });
    },
  });
}

/**
 * Update offering status (cancel/complete)
 */
export function useUpdateOfferingStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      offeringId,
      status,
    }: {
      offeringId: string;
      status: TrainingOfferingInstance['status'];
    }) => {
      const is_active = ['upcoming', 'ongoing'].includes(status);
      const { error } = await supabase
        .from('training_offering_instances')
        .update({ status, is_active })
        .eq('id', offeringId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offering-instances'] });
    },
  });
}

/**
 * Delete an offering instance
 */
export function useDeleteOffering() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (offeringId: string) => {
      const { error } = await supabase
        .from('training_offering_instances')
        .delete()
        .eq('id', offeringId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offering-instances'] });
    },
  });
}
